import type { App } from "../app.js";
import { memberName } from "../app.js";
import { formatDate, h, notice } from "../dom.js";
import { COVER_CHECKS, METHOD_LABEL, type ClaimReview, type CoverCheck, type CoverCheckId, type ReviewMethod } from "../prepare/model.js";
import type { Hook, Issue } from "../prepare/scenario.js";
import { CLAIM_STATE_LABEL, claimHash, excerptHints, type ClaimState } from "../prepare/status.js";

/** Pieces shown both while preparing and in the editor of a built project. */

const STATE_CLASS: Record<ClaimState["state"], string> = { confirmed: "ok", rejected: "bad", to_verify: "warn", review_outdated: "warn" };

/** The checks' result: what blocks, what to look at, what is only said. */
export function issuesView(issues: Issue[]): HTMLElement {
  const blocking = issues.filter((i) => i.level === "blocking");
  const warnings = issues.filter((i) => i.level === "warning");
  const infos = issues.filter((i) => i.level === "info");
  return h(
    "div",
    { class: "pp-issues" },
    blocking.length
      ? notice("bad", `${blocking.length} point(s) bloquant(s)`, h("ul", { "data-issues": "blocking" }, blocking.map((i) => h("li", { "data-code": i.code }, i.message))))
      : notice("ok", "Aucun point bloquant", "La préparation peut devenir un projet ; les avertissements ci-dessous restent à regarder."),
    warnings.length ? notice("warn", `${warnings.length} avertissement(s)`, h("ul", { "data-issues": "warning" }, warnings.map((i) => h("li", { "data-code": i.code }, i.message)))) : null,
    infos.length ? h("ul", { class: "muted pp-infos", "data-issues": "info" }, infos.map((i) => h("li", { "data-code": i.code }, i.message))) : null,
  );
}

/** An image brief as written by the conversation (kept while images are added). */
export function briefView(brief: Record<string, unknown> | null): HTMLElement {
  if (!brief) return h("p", { class: "muted pp-brief" }, "Aucun brief d'image pour cette slide.");
  const rows: Array<[string, string]> = [
    ["Montre", "shows"],
    ["Pourquoi", "why"],
    ["Sujet", "subject"],
    ["Composition", "composition"],
    ["Espace pour le texte", "negative_space"],
    ["Réalisme", "realism"],
    ["Lumière", "light"],
    ["Cadrage", "framing"],
    ["Point de vue", "viewpoint"],
    ["Matières", "texture"],
  ];
  const list = (key: string) => (Array.isArray(brief[key]) ? (brief[key] as unknown[]).filter((v) => typeof v === "string").join(" ; ") : "");
  return h(
    "dl",
    { class: "pp-brief" },
    rows.filter(([, key]) => typeof brief[key] === "string" && brief[key]).map(([label, key]) => [h("dt", null, label), h("dd", null, String(brief[key]))]),
    list("constraints") ? [h("dt", null, "Contraintes"), h("dd", null, list("constraints"))] : null,
    list("avoid") ? [h("dt", null, "À éviter"), h("dd", null, list("avoid"))] : null,
  );
}

export const claimChip = (c: ClaimState) => h("span", { class: `chip ${STATE_CLASS[c.state]}`, "data-claim-state": c.state }, CLAIM_STATE_LABEL[c.state]);

/**
 * The claims with their sources, and the reading form: a fact counts as verified only when a
 * member says how they read it and copies the passage. What the conversation wrote is shown
 * apart, as a proposal.
 */
export function claimsView(app: App, states: ClaimState[], onReview: (review: ClaimReview) => void, sources: Array<{ url: string; title: string }>): HTMLElement {
  if (!states.length) return h("p", { class: "muted" }, "Aucune affirmation dans la réponse.");
  const titleOf = new Map(sources.map((s) => [s.url, s.title]));
  return h(
    "ol",
    { class: "pp-claims" },
    states.map((c) => {
      const excerpt = h("textarea", { rows: "3", maxlength: "2000", "aria-label": `Passage lu pour ${c.claim.id}`, placeholder: "Copie ici le passage de la source qui établit ce fait." });
      const note = h("input", { type: "text", maxlength: "600", "aria-label": `Note sur ${c.claim.id}`, placeholder: "Note (facultative)" });
      const method = h("select", { "aria-label": `Méthode de lecture pour ${c.claim.id}` }, (Object.keys(METHOD_LABEL) as ReviewMethod[]).map((m) => h("option", { value: m }, METHOD_LABEL[m])));
      const source = h("select", { "aria-label": `Source lue pour ${c.claim.id}` }, c.claim.source_urls.map((u) => h("option", { value: u }, titleOf.get(u) ?? u)));
      const hints = h("p", { class: "muted pp-hints", "aria-live": "polite" });
      const error = h("p", { class: "pp-error", role: "alert" });
      const showHints = () => {
        const { numbers, note: limit } = excerptHints(c.claim.text, excerpt.value);
        hints.textContent = excerpt.value.trim() ? `${numbers.length ? `Nombres de l'affirmation retrouvés dans le passage : ${numbers.map((n) => `${n.value} ${n.found ? "✓" : "✗"}`).join(", ")}. ` : ""}${limit}` : "";
      };
      excerpt.addEventListener("input", showHints);
      const record = (decision: ClaimReview["decision"]) => {
        if (!excerpt.value.trim()) {
          error.textContent = "Copie le passage lu dans la source : sans lui, rien n'est enregistré.";
          excerpt.focus();
          return;
        }
        if (!source.value) {
          error.textContent = "Cette affirmation ne cite aucune source.";
          return;
        }
        onReview({ claim_id: c.claim.id, claim_hash: claimHash(c.claim), decision, method: method.value as ReviewMethod, source_url: source.value, excerpt: excerpt.value.trim(), note: note.value.trim(), by: app.member.user_id, at: new Date().toISOString() });
      };
      return h(
        "li",
        { class: "pp-claim", "data-claim": c.claim.id },
        h("div", { class: "row" }, h("strong", null, c.claim.id), claimChip(c), c.slides.length ? h("span", { class: "chip" }, `slide(s) ${c.slides.join(", ")}`) : h("span", { class: "chip" }, "non affichée"), c.claim.usage !== "use" ? h("span", { class: "chip" }, `usage : ${c.claim.usage}`) : null),
        h("p", { class: "pp-claim-text" }, c.claim.text),
        h(
          "ul",
          { class: "pp-sources" },
          c.claim.source_urls.map((u) => h("li", null, h("a", { href: u, target: "_blank", rel: "noopener noreferrer" }, titleOf.get(u) ?? u), titleOf.has(u) ? null : h("span", { class: "muted" }, " (absente de la liste des sources)"))),
        ),
        h("p", { class: "muted pp-proposal" }, `Statut écrit par la conversation : « ${c.conversation} »${c.conversation === "verified" ? " — une proposition, pas une validation" : ""}.`),
        c.review
          ? h(
              "div",
              { class: "pp-review" },
              h("p", null, `${c.review.decision === "confirmed" ? "Confirmé" : "Rejeté"} par ${memberName(app, c.review.by)} le ${formatDate(c.review.at)} — ${METHOD_LABEL[c.review.method]}${c.state === "review_outdated" ? " (lecture faite sur un texte qui a changé depuis : à refaire)" : ""}.`),
              h("blockquote", null, c.review.excerpt),
              c.review.note ? h("p", { class: "muted" }, c.review.note) : null,
            )
          : null,
        h(
          "details",
          { class: "pp-read", open: c.state !== "confirmed" && c.state !== "rejected" && c.slides.length > 0 ? true : null },
          h("summary", null, c.review ? "Lire à nouveau" : "Lire la source et confirmer"),
          h("label", { class: "field" }, "Méthode", method),
          h("label", { class: "field" }, "Source ouverte", source),
          h("label", { class: "field" }, "Passage lu (copié de la source)", excerpt),
          note,
          hints,
          error,
          h("div", { class: "row" }, h("button", { class: "btn primary", type: "button", onclick: () => record("confirmed") }, `Confirmer ${c.claim.id}`), h("button", { class: "btn", type: "button", onclick: () => record("rejected") }, `Rejeter ${c.claim.id}`)),
        ),
      );
    }),
  );
}

/** The human cover check: the chosen title and visual concept, nine questions, no score. */
export function coverCheckView(app: App, hook: Hook | null, coverTitle: string, current: CoverCheck | null, state: "none" | "valid" | "partial" | "outdated", onSave: (items: Record<CoverCheckId, boolean>, note: string) => void): HTMLElement {
  const boxes = new Map<CoverCheckId, HTMLInputElement>();
  const note = h("input", { type: "text", maxlength: "600", "aria-label": "Note du contrôle de couverture", placeholder: "Note (facultative)", value: current?.note ?? "" });
  return h(
    "div",
    { class: "pp-cover" },
    h("p", null, h("strong", null, "Titre choisi : "), coverTitle || "(vide)"),
    hook ? h("p", null, h("strong", null, "Concept visuel : "), hook.visual_concept) : h("p", { class: "muted" }, "Aucun concept visuel enregistré."),
    state === "outdated" ? h("p", { class: "chip warn" }, "Couverture modifiée depuis le dernier contrôle : à refaire") : null,
    current ? h("p", { class: "muted" }, `Dernier contrôle : ${memberName(app, current.by)}, ${formatDate(current.at)}.`) : null,
    h(
      "fieldset",
      { class: "pp-checks" },
      h("legend", null, "À regarder sur la couverture (aperçu et vue téléphone)"),
      COVER_CHECKS.map(([id, label]) => {
        const box = h("input", { type: "checkbox", checked: state !== "outdated" && current?.items[id] ? true : null });
        boxes.set(id, box);
        return h("label", { class: "st-check" }, box, h("span", null, label));
      }),
    ),
    note,
    h("p", { class: "muted st-help" }, "Contrôle humain : aucune note automatique, aucun modèle de vision."),
    h("button", { class: "btn", type: "button", onclick: () => onSave(Object.fromEntries(COVER_CHECKS.map(([id]) => [id, boxes.get(id)!.checked])) as Record<CoverCheckId, boolean>, note.value.trim()) }, "Enregistrer le contrôle de couverture"),
  );
}

/** « Copier le brief d'image »: the brief as plain text in the clipboard (selected for a manual copy when refused). */
export function copyBriefButton(text: () => string, slide: number, label = "Copier le brief d'image", areaLabel = `Brief d'image de la slide ${slide}`): HTMLElement {
  const status = h("span", { class: "muted pp-copied", "aria-live": "polite" });
  const area = h("textarea", { readonly: true, rows: "6", class: "pp-dossier", hidden: true, "aria-label": areaLabel });
  return h(
    "div",
    { class: "pp-copy" },
    h(
      "button",
      {
        class: "btn",
        type: "button",
        onclick: async () => {
          const value = text();
          try {
            await navigator.clipboard.writeText(value);
            area.hidden = true;
            status.textContent = "Brief copié.";
          } catch {
            area.value = value;
            area.hidden = false;
            area.select();
            status.textContent = "Copie automatique refusée par le navigateur : le brief est sélectionné ci-dessous, copie-le (Ctrl+C).";
          }
        },
      },
      label,
    ),
    status,
    area,
  );
}
